/**
 * MeasureTool.tsx — قياس المسافة والمساحة على الخريطة (بأسلوب Zoom Earth).
 *
 * الرسم بـ SVG فوق الخريطة لا بطبقة MapLibre مقصود: مكدّس الطبقات هنا مُرتَّب
 * بصرامة (راجع enforceWeatherStackOrder في webgl/layerOrder.ts) وكل طبقة جديدة
 * تُدسّ فيه تُصبح حالةً يجب أن يعرفها ذلك الترتيب. أداةٌ مؤقّتة يفتحها المستخدم
 * ويغلقها لا ينبغي أن تُعدّل عقد الطبقات الدائم. وطبقةٌ فوق الكل تُعاد إسقاطها مع
 * حركة الخريطة تكلّف هنا إسقاط بضع نقاط لا أكثر.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FiX, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { MdStraighten, MdOutlineSquareFoot } from 'react-icons/md';
import { useShallow } from 'zustand/react/shallow';
import type { MapMouseEvent } from 'maplibre-gl';
import { useWeatherStore } from '../../store/weatherStore';
import { useMapRef } from './MapContext';
import { useTranslation } from '../../i18n/LanguageContext';
import {
    densifyPath, formatArea, formatDistance, pathLengthMeters, sphericalAreaM2, type LngLat,
} from './measureGeo';

/** لون القياس — لون الموقع الأساسي نفسه (--color-primary). مكتوبٌ هنا صراحةً لا
 *  عبر var(): SVG داخل React يقبل var() في الـ stroke، لكننا نشتقّ منه الشفافية
 *  بلاصقة سداسية (`${LINE_COLOR}2e`) وهو ما لا يعمل مع متغيّر CSS. */
const LINE_COLOR = '#667eea';

/** أقصر إزاحة بالبكسل تفصل «نقرة» عن «سحب الخريطة» — دون ذلك نتجاهل الحدث. */
const DRAG_SLOP_PX = 4;

export const MeasureTool = memo(function MeasureTool() {
    const { t, locale } = useTranslation();
    const mapRef = useMapRef();
    const { measureMode, setMeasureMode, windUnit } = useWeatherStore(useShallow((s) => ({
        measureMode: s.measureMode,
        setMeasureMode: s.setMeasureMode,
        windUnit: s.units.wind,
    })));

    const [points, setPoints]   = useState<LngLat[]>([]);
    const [hover, setHover]     = useState<LngLat | null>(null);
    const [finished, setFinished] = useState(false);
    // يتغيّر مع كل إطار تحرّكت فيه الخريطة فيُعاد الإسقاط. القيمة نفسها بلا معنى.
    const [, bump] = useState(0);

    const active = measureMode !== 'none';
    // لا توجد وحدة مسافة مستقلّة في التفضيلات، فنشتقّها من وحدة الرياح: من اختار
    // mph يقرأ الأميال في كل مكان آخر بالموقع، فأن نُعطيه الكيلومترات هنا وحدها
    // تناقضٌ داخل الواجهة نفسها.
    const imperial = windUnit === 'mph';

    // ── دورة حياة الوضع ──────────────────────────────────────────────────────
    useEffect(() => {
        if (!active) { setPoints([]); setHover(null); setFinished(false); }
    }, [active]);

    // تبديل مسافة⇄مساحة يبدأ شكلاً جديداً: مسارٌ مفتوح ومضلّع مغلق ليسا الشيء نفسه.
    useEffect(() => { setPoints([]); setHover(null); setFinished(false); }, [measureMode]);

    // النقرة التالية بعد الإنهاء تبدأ شكلاً جديداً، لكن onClick أعلاه مُثبَّت عند أول
    // تركيب (تبعيّاته لا تشمل finished عمداً كي لا نفكّ الاشتراكات مع كل نقرة).
    const finishedRef = useRef(false);
    useEffect(() => { finishedRef.current = finished; }, [finished]);

    const undo  = useCallback(() => { setPoints((p) => p.slice(0, -1)); setFinished(false); }, []);
    const clear = useCallback(() => { setPoints([]); setHover(null); setFinished(false); }, []);

    // ── ربط أحداث الخريطة ────────────────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !active) return;

        const canvas = map.getCanvas();
        const prevCursor = canvas.style.cursor;
        canvas.style.cursor = 'crosshair';
        // بدون هذا يقفز التكبير مع كل نقرة مزدوجة تُنهي الرسم.
        const hadDblClickZoom = map.doubleClickZoom.isEnabled();
        map.doubleClickZoom.disable();

        // نميّز النقرة من السحب بأنفسنا: MapLibre يُطلق click بعد سحبةٍ قصيرة أيضاً،
        // فكانت كل محاولة لتحريك الخريطة أثناء الرسم تُسقط نقطةً لم يطلبها أحد.
        let downAt: { x: number; y: number } | null = null;
        const onDown = (e: MapMouseEvent) => { downAt = { x: e.point.x, y: e.point.y }; };

        const onClick = (e: MapMouseEvent) => {
            if (downAt && Math.hypot(e.point.x - downAt.x, e.point.y - downAt.y) > DRAG_SLOP_PX) return;
            setPoints((prev) => (finishedRef.current ? [{ lng: e.lngLat.lng, lat: e.lngLat.lat }]
                                                     : [...prev, { lng: e.lngLat.lng, lat: e.lngLat.lat }]));
            setFinished(false);
        };

        const onDblClick = () => { setFinished(true); setHover(null); };
        const onMove     = (e: MapMouseEvent) => setHover({ lng: e.lngLat.lng, lat: e.lngLat.lat });
        const onLeave    = () => setHover(null);
        const reproject  = () => bump((n) => n + 1);

        map.on('mousedown', onDown);
        map.on('click', onClick);
        map.on('dblclick', onDblClick);
        map.on('mousemove', onMove);
        map.on('mouseout', onLeave);
        map.on('move', reproject);
        map.on('resize', reproject);

        return () => {
            map.off('mousedown', onDown);
            map.off('click', onClick);
            map.off('dblclick', onDblClick);
            map.off('mousemove', onMove);
            map.off('mouseout', onLeave);
            map.off('move', reproject);
            map.off('resize', reproject);
            canvas.style.cursor = prevCursor;
            if (hadDblClickZoom) map.doubleClickZoom.enable();
        };
    }, [mapRef, active]);

    // ── إغلاق بـ Escape ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!active) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            if (points.length) clear(); else setMeasureMode('none');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, points.length, clear, setMeasureMode]);

    if (!active) return null;

    const map = mapRef.current?.getMap();
    const isArea = measureMode === 'area';

    // نقاط الرسم = المثبّتة + نقطة المؤشّر (الشريط المطّاطي) ما لم يُنهِ المستخدم.
    const draft: LngLat[] = finished || !hover ? points : [...points, hover];
    const project = (p: LngLat) => map?.project([p.lng, p.lat]) ?? { x: 0, y: 0 };
    const screen  = (list: LngLat[]) => densifyPath(list).map(project);

    const ring = isArea && draft.length > 2 ? [...draft, draft[0]] : draft;
    const pts  = map ? screen(ring) : [];
    const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

    // القيمة تُحسب من النقاط المثبّتة **ومعها** نقطة المؤشّر: قراءةُ الرقم وهو يتغيّر
    // تحت المؤشّر هي نصف فائدة الأداة (تحرّك حتى يصل الرقم إلى ما تريد ثم انقر).
    const measured = isArea
        ? (draft.length >= 3 ? formatArea(sphericalAreaM2(draft), imperial, locale) : null)
        : (draft.length >= 2 ? formatDistance(pathLengthMeters(draft), imperial, locale) : null);

    // موضع الشارة: مركز المضلّع للمساحة، ومنتصف الضلع الأخير للمسافة — كما عند
    // Zoom Earth، فتبقى الشارة ملتصقةً بما يقيسه المستخدم الآن لا بعيدةً عنه.
    let badge: { x: number; y: number } | null = null;
    if (measured && map) {
        if (isArea) {
            const v = draft.map(project);
            badge = { x: v.reduce((s, p) => s + p.x, 0) / v.length, y: v.reduce((s, p) => s + p.y, 0) / v.length };
        } else {
            const a = project(draft[draft.length - 2]);
            const b = project(draft[draft.length - 1]);
            badge = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        }
    }

    const Icon = isArea ? MdOutlineSquareFoot : MdStraighten;

    return (
        <>
            {/* الرسم — لا يلتقط المؤشّر إطلاقاً كي تبقى الخريطة تحته قابلة للنقر والسحب */}
            <svg
                className="pointer-events-none absolute inset-0 z-[760] h-full w-full"
                aria-hidden="true"
            >
                {pts.length > 1 && (
                    <>
                        {isArea && draft.length > 2 && (
                            <path d={`${path} Z`} fill={`${LINE_COLOR}2e`} stroke="none" />
                        )}
                        {/* خطّ واحد بلون الموقع، تحته هالةٌ من اللون نفسه بشفافية عالية.
                            الهالة ليست زينة: الخطّ الرفيع وحده يذوب فوق حقلٍ قريب من لونه
                            (البنفسجي فوق مناطق الضغط مثلاً)، والهالة تفصله عمّا تحته بلا
                            إقحام لونٍ ثانٍ يكسر هوية الواجهة. */}
                        <path d={path} fill="none" stroke={LINE_COLOR} strokeOpacity={0.28}
                              strokeWidth={10} strokeLinecap="round" strokeLinejoin="round" />
                        <path d={path} fill="none" stroke={LINE_COLOR} strokeWidth={3.5}
                              strokeLinecap="round" strokeLinejoin="round" />
                    </>
                )}
                {points.map(project).map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={5.5} fill="#ffffff" stroke={LINE_COLOR} strokeWidth={3} />
                ))}
            </svg>

            {/* شارة القيمة على الخريطة */}
            {badge && measured && (
                <div
                    className="pointer-events-none absolute z-[770] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-[#1c2230] px-2.5 py-1.5 text-[13px] font-extrabold text-white shadow-[0_4px_16px_rgba(0,0,0,0.45)] ring-1 ring-[#667eea]/60 [direction:ltr] [unicode-bidi:isolate] whitespace-nowrap"
                    style={{ left: badge.x, top: badge.y }}
                >
                    {measured}
                </div>
            )}

            {/* موضع البطاقة — الحافّة **البادئة** لا الخاتمة على الديسكتوب: العمود الخاتم
                مشغولٌ بالكامل (مؤشّر الإحداثيات top:15، زرّ التراكبات السريعة top:112،
                ومفتاح الألوان أسفله)، فأيّ بطاقة هناك تغطّي واحداً منها مهما ضُبط
                ارتفاعها. البادئة السفلى تحمل مبدّل النموذج وحده (bottom:20، ارتفاع ~40)
                فتكفيه إزاحة 76 بكسل. وعلى الجوّال ينقلب الأمر: الأسفل مزدحم (الشريط
                الزمني والتراكبات وشريط الأدوات) والأعلى فارغٌ تحت الشعار والمفتاح. */}
            <div className="absolute bottom-[76px] start-4 z-[780] w-[236px] max-w-[calc(100vw-36px)] overflow-hidden rounded-xl border border-[var(--panel-border)] bg-[var(--panel-bg)] text-inherit shadow-[var(--panel-shadow)] backdrop-blur-[20px] animate-fade-in-right light:text-[var(--text-primary)] max-[768px]:bottom-auto max-[768px]:top-[84px] max-[768px]:start-3 max-[768px]:end-3 max-[768px]:w-auto max-[768px]:max-w-none">
                <div className="flex items-center gap-2 border-b border-[var(--divider)] px-3 py-2.5">
                    <Icon className="shrink-0 text-base text-[var(--color-primary)]" />
                    <h3 className="m-0 flex-1 text-[13px] font-extrabold">
                        {t(isArea ? 'measure.areaTitle' : 'measure.distanceTitle')}
                    </h3>
                    <button
                        className="flex h-6 w-6 items-center justify-center rounded-md border-none bg-white/[0.08] text-xs text-inherit opacity-70 transition-all duration-150 hover:bg-[rgba(255,80,80,0.18)] hover:text-[#ff5050] hover:opacity-100"
                        onClick={() => setMeasureMode('none')}
                        title={t('common.close')}
                        aria-label={t('common.close')}
                    >
                        <FiX />
                    </button>
                </div>

                <p className="m-0 px-3 py-2.5 text-[11.5px] leading-[1.6] opacity-75">
                    {t('measure.hint')}
                </p>

                {/* النتيجة مكرّرة هنا لا على الخريطة وحدها: شارة الخريطة قد تخرج خارج
                    الإطار مع التحريك، والبطاقة ثابتة. */}
                <div className="flex items-center justify-between gap-2 border-t border-[var(--divider)] px-3 py-2">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wide opacity-55">
                        {t(isArea ? 'measure.area' : 'measure.distance')}
                    </span>
                    <span className="text-[15px] font-extrabold [direction:ltr] [unicode-bidi:isolate]">
                        {measured ?? '—'}
                    </span>
                </div>

                <div className="flex gap-1.5 border-t border-[var(--divider)] px-2 py-2">
                    <button
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--border-color)] bg-transparent px-2 py-1.5 text-[11.5px] font-bold text-inherit transition-colors duration-150 hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-35 light:hover:bg-[var(--row-hover)]"
                        onClick={undo}
                        disabled={points.length === 0}
                    >
                        <FiRotateCcw /> {t('measure.undo')}
                    </button>
                    <button
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--border-color)] bg-transparent px-2 py-1.5 text-[11.5px] font-bold text-inherit transition-colors duration-150 hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-35 light:hover:bg-[var(--row-hover)]"
                        onClick={clear}
                        disabled={points.length === 0}
                    >
                        <FiTrash2 /> {t('measure.clear')}
                    </button>
                </div>
            </div>
        </>
    );
});
